import { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getInvoicePaginatedAction } from './API/Action';
import CustomChip from '../Components/CustomChip';
import InvoiceCard from './Components/InvoiceCard';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import StickyEntity from '../Common/Entity/StickyEntity';

const PAGE_SIZE = 10;

const InvoiceList = () => {
  const [dateFilter, setDateFilter] = useState('');

  const { selectedSupplierData, selectedEntity: selectedCompanyEntity } = useSelector(
    (state) => state.entityReducer,
  );

  const dateFilters = [
    { value: '1week', label: 'Past 1 week' },
    { value: '1month', label: 'Past 1 month' },
    { value: '3months', label: 'Past 3 months' },
  ];

  const getStartEndDate = useCallback(() => {
    const now = dayjs();
    switch (dateFilter) {
      case '1week':
        return {
          startDate: now.subtract(1, 'week').toDate(),
          endDate: now.toDate(),
        };
      case '1month':
        return {
          startDate: now.subtract(1, 'month').toDate(),
          endDate: now.toDate(),
        };
      case '3months':
        return {
          startDate: now.subtract(3, 'month').toDate(),
          endDate: now.toDate(),
        };

      default:
        return { startDate: null, endDate: null };
    }
  }, [dateFilter]);

  const fetchInvoiceList = useCallback(
    async (page, pageSize) => {
      const config = new URLSearchParams();
      config.append('f', 'id$gte$0');
      config.append('o', 'id$desc');
      config.append('page', page.toString());
      config.append('page_size', pageSize.toString());

      if (selectedCompanyEntity) {
        config.append('f', `company_esi_id$eq$${selectedCompanyEntity}`);
      }

      const startEndDate = getStartEndDate();
      if (startEndDate.startDate && startEndDate.endDate) {
        config.append(
          'f',
          `date_of_invoice$gte$${dayjs(startEndDate.startDate)
            .startOf('day')
            .format('YYYY-MM-DD HH:mm:ssZ')}`,
        );
        config.append(
          'f',
          `date_of_invoice$lte$${dayjs(startEndDate.endDate)
            .endOf('day')
            .format('YYYY-MM-DD HH:mm:ssZ')}`,
        );
      }

      try {
        const result = await getInvoicePaginatedAction(config);
        if (result.error) {
          toast.error(result.message || 'Failed to fetch invoices');
        }
        return result;
      } catch (error) {
        toast.error('Failed to fetch invoices');
        return { error: true, data: [] };
      }
    },
    [selectedCompanyEntity, getStartEndDate],
  );

  const { data: invoiceList, loading } = useInfiniteScroll({
    fetchFunction: fetchInvoiceList,
    pageSize: PAGE_SIZE,
    dependencies: selectedCompanyEntity
      ? [selectedCompanyEntity, dateFilter]
      : [],
  });
  const isEntityEnabled = selectedSupplierData?.entity_enabled;
  return (
    <>
      <StickyEntity />
      <Box sx={{ mt: {xs : isEntityEnabled ? '96px': 0, md: 0 }, p: { xs: 2, md: 2 }, pt: { xs: 2, md: 0 } }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              pt: { xs: 0, md: 2 },
              overflowX: 'scroll',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {[
              ...dateFilters.map((filter) => ({
                key: filter.value,
                label: filter.label,
                selected: dateFilter === filter.value,
                onClick: () => setDateFilter(filter.value),
                onDelete:
                  dateFilter === filter.value
                    ? () => setDateFilter('')
                    : undefined,
              })),
            ]
              .sort((a, b) => b.selected - a.selected) // Selected chips first
              .map((chip) => (
                <CustomChip
                  key={chip.key}
                  label={chip.label}
                  onClick={chip.onClick}
                  onDelete={chip.onDelete}
                  selected={chip.selected}
                  variant={chip.selected ? 'filled' : 'outlined'}
                />
              ))}
          </Box>
          <InvoiceCard
            invoiceData={invoiceList}
            loading={loading}
          />
        </Box>
      </Box>
    </>
  );
};

export default InvoiceList;
